/**
 * IXD 값 편집기 - URL 로 들어온 워크북(선택 화면이 연 탭·예전 preview 링크) 해석.
 *
 * `workbookId` 만 들고 오므로 워크북을 한 번 읽어 어느 재무제표 유형 칸에 꽂을지 정한다.
 * 이 조회가 실패하면 비교 자체가 시작되지 않아 `useWorkbookCompare` 가 꺼진 채로 남고
 * ready/failed 를 아무도 못 보낸다 — 선택 화면이 "비교 준비 중"에 갇히지 않도록
 * 실패를 여기서 알린다.
 */

import { useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

import queries from "@/queries";
import type { FsType } from "@/apis/ixd-editor/types";
import { notifyIxdMatchPreviewOpener } from "@/utils/ixd-workbook-match/tab-messages";

/**
 * 워크북 유형 → 비교에 쓸 재무제표 유형.
 *
 * 백엔드 비교 프리뷰가 `is_consolidated = (type == CONSOLIDATED)` 로 TOC 를 걸러
 * 내리므로(ixd_match_preview_app_service) 프런트도 같은 식이어야 한다. CONSOLIDATED
 * 가 아닌 값(BOTH·미상)을 연결로 보면 TOC 판정이 엇갈려 비교 본문이 빈 화면이 된다.
 */
export const toFsType = (financialStatementType: string | null): FsType =>
  financialStatementType === "CONSOLIDATED" ? "consolidated" : "separate";

/**
 * 고른 워크북을 URL 에 적어 두는 파라미터 — 유형을 이름에 담는다.
 * 복원할 때 어느 칸인지 알 수 있어 워크북을 다시 읽지 않아도 된다
 * (`workbookId` 는 유형을 모르는 외부 진입용이라 조회가 필요하다).
 */
const SELECTED_WORKBOOK_PARAM: Record<FsType, string> = {
  consolidated: "consolidatedWorkbookId",
  separate: "separateWorkbookId",
};

const PARAM_ENTRIES = Object.entries(SELECTED_WORKBOOK_PARAM) as [
  FsType,
  string,
][];

/** URL 에 적힌 유형별 워크북 선택 */
export const readSelectedWorkbooks = (
  params: URLSearchParams,
): Partial<Record<FsType, string>> => {
  const selected: Partial<Record<FsType, string>> = {};
  for (const [fsType, param] of PARAM_ENTRIES) {
    const id = params.get(param);
    if (id) selected[fsType] = id;
  }
  return selected;
};

/**
 * 선택을 반영한 새 파라미터. 바뀐 게 없으면 null —
 * 호출부가 쓸데없는 history 교체를 건너뛸 수 있다.
 */
export const withSelectedWorkbooks = (
  params: URLSearchParams,
  selected: Partial<Record<FsType, string>>,
): URLSearchParams | null => {
  const next = new URLSearchParams(params);
  for (const [fsType, param] of PARAM_ENTRIES) {
    const id = selected[fsType];
    if (id) next.set(param, id);
    else next.delete(param);
  }
  return next.toString() === params.toString() ? null : next;
};

export const useUrlWorkbook = (urlWorkbookId: string | null) => {
  const { data, isError, refetch } = useQuery({
    ...queries.workbook(urlWorkbookId ?? ""),
    enabled: !!urlWorkbookId,
    staleTime: Infinity,
  });

  const hasError = !!urlWorkbookId && isError;
  useEffect(() => {
    if (hasError) notifyIxdMatchPreviewOpener("failed");
  }, [hasError]);

  const retry = useCallback(() => void refetch(), [refetch]);

  return {
    /** 워크북을 읽기 전에는 null — 유형을 모르는 동안 비교를 시작하지 않는다 */
    fsType: data ? toFsType(data.financialStatementType) : null,
    hasError,
    retry,
  };
};
